/**
 * The handover between the login routes (09 § 4.1).
 *
 * `sendOtp` answers with a `methodId`, and `verifyOtp` wants that id rather
 * than the address — so it has to outlive the navigation to `/login/code`, and
 * a reload there with the code still sitting in the user's mail app must find
 * it too. The same goes for the token of a detour on the way to `/login/totp`.
 *
 * `sessionStorage` is the narrowest place that manages both: one tab, gone when
 * the tab closes. None of these values is a credential on its own — the code
 * the user types is.
 */

const KEY = 'cowoo.login.v1';

export interface PendingLogin {
  email: string;
  methodId: string;
  /** Set once a login call answered with a detour (04 § 11.2). */
  detour?: { kind: 'totp_required' | 'challenge'; token: string };
}

function write(value: PendingLogin): void {
  sessionStorage.setItem(KEY, JSON.stringify(value));
}

export function rememberOtpMethod(email: string, methodId: string): void {
  write({ email, methodId });
}

export function rememberDetour(detour: NonNullable<PendingLogin['detour']>): void {
  const current = readPendingLogin();
  write({ email: current?.email ?? '', methodId: current?.methodId ?? '', detour });
}

export function readPendingLogin(): PendingLogin | null {
  const raw = sessionStorage.getItem(KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingLogin>;
    if (typeof parsed.email !== 'string' || typeof parsed.methodId !== 'string') return null;
    return { email: parsed.email, methodId: parsed.methodId, detour: parsed.detour };
  } catch {
    return null;
  }
}

export function forgetPendingLogin(): void {
  sessionStorage.removeItem(KEY);
}

/** Where to go once the session exists (04 § 11.3); the funnel start by default. */
export function takeReturnTo(): string {
  const stored = sessionStorage.getItem('cowoo.returnTo');
  sessionStorage.removeItem('cowoo.returnTo');
  return stored ?? '/start';
}
